/**
 * classifyParty — single source of truth for party classification.
 *
 * Derives the `org_subtype` + `privacy_class` from a party's identifier
 * per the rule table in ADR-0018 (`docs/decisions/0018-party-model.md`).
 * Used by:
 *   - the contract editor's PartiesPanel (live classify on input)
 *   - the R2 upload-and-extract pipeline (classify each extracted entity)
 *   - the Bolagsverket lookup result mapper
 *   - the predefined-catalog seed
 *
 * Conservative default: when in doubt, return a non-searchable class.
 * Better to make the user re-type a name than leak PII into search.
 *
 * Independent of any DB / framework — pure function. The Drizzle enums
 * in `@regta/regta-data` mirror these literal unions one-to-one.
 */

export type PartyIdType = 'org_no' | 'person_no' | 'foreign' | 'none';

export type PartyOrgSubtype =
  | 'ab'
  | 'kb'
  | 'hb'
  | 'municipality'
  | 'state'
  | 'foundation'
  | 'economic_assoc'
  | 'ideal_assoc'
  | 'enskild_firma'
  | 'other';

export type PartyPrivacyClass =
  | 'searchable_org'
  | 'pii_org'
  | 'pii_individual'
  | 'unknown';

export type ClassifyPartyInput = {
  id_type: PartyIdType;
  id_number: string | null | undefined;
};

export type ClassifyPartyResult = {
  org_subtype: PartyOrgSubtype | null;
  privacy_class: PartyPrivacyClass;
};

/** Strip dashes, spaces, and underscores to compare against numeric prefixes. */
function normalizeIdNumber(raw: string): string {
  return raw.replace(/[\s\-_]/g, '');
}

/**
 * Swedish org numbers are 10 digits, format `XXXXXX-XXXX`. The first
 * digit (after normalization) plus, for the municipality range, the
 * second, determines the entity kind. Sources: SCB / Bolagsverket
 * documentation. Cited in ADR-0018.
 */
function classifyOrgNo(idNumber: string): ClassifyPartyResult {
  const n = normalizeIdNumber(idNumber);
  // Must be 10 digits, all numeric, to be a Swedish org_no.
  if (!/^\d{10}$/.test(n)) {
    // Treat as foreign-shaped — caller may want to switch id_type to
    // 'foreign'; we stay conservative here.
    return { org_subtype: 'other', privacy_class: 'pii_org' };
  }
  // Municipality range is "21XX...".
  if (n.startsWith('21')) {
    return { org_subtype: 'municipality', privacy_class: 'searchable_org' };
  }
  switch (n[0]) {
    case '0':
      // Sole trader — 1:1 with a person, PII.
      return { org_subtype: 'enskild_firma', privacy_class: 'pii_individual' };
    case '2':
      // Non-municipality state body (e.g. 22XX state, 23-29 other state).
      return { org_subtype: 'state', privacy_class: 'searchable_org' };
    case '5':
      return { org_subtype: 'ab', privacy_class: 'searchable_org' };
    case '6':
      // Trust/insurance — surface as searchable_org with subtype 'other'.
      return { org_subtype: 'other', privacy_class: 'searchable_org' };
    case '7':
      return { org_subtype: 'economic_assoc', privacy_class: 'searchable_org' };
    case '8':
      // 8* covers ideal associations AND foundations — ambiguous from the
      // number alone. We bias to `ideal_assoc` and let the user override
      // (subtype is editable in the form). Both are `searchable_org`.
      return { org_subtype: 'ideal_assoc', privacy_class: 'searchable_org' };
    case '9':
      // 9* covers both KB and HB — ambiguous from the number. Bias to
      // `kb`; editable downstream.
      return { org_subtype: 'kb', privacy_class: 'searchable_org' };
    default:
      // 1, 3, 4 — out-of-range prefixes; conservative default.
      return { org_subtype: 'other', privacy_class: 'pii_org' };
  }
}

/**
 * Personnummer pattern allows YYYYMMDD-XXXX, YYMMDD-XXXX, or 10/12 digits.
 * We accept any string — classification is fixed (pii_individual);
 * validation is the form's job.
 */
function classifyPersonNo(): ClassifyPartyResult {
  return { org_subtype: null, privacy_class: 'pii_individual' };
}

export function classifyParty(input: ClassifyPartyInput): ClassifyPartyResult {
  const id = input.id_number?.trim() ?? '';

  if (input.id_type === 'none' || id === '') {
    return { org_subtype: null, privacy_class: 'unknown' };
  }
  if (input.id_type === 'person_no') {
    return classifyPersonNo();
  }
  if (input.id_type === 'foreign') {
    // Foreign orgs are non-public-record from our vantage point —
    // conservative default until manual confirmation.
    return { org_subtype: 'other', privacy_class: 'pii_org' };
  }
  // id_type === 'org_no'
  return classifyOrgNo(id);
}
